import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  Content as CollapsibleContent,
  Root as CollapsibleRoot,
  Trigger as CollapsibleTrigger,
} from '@radix-ui/react-collapsible';
import { Content, Portal, Root, Trigger } from '@radix-ui/react-popover';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import type { DropdownMenuAlignmentOptions } from './DropdownMenu';

export type NestedOption = {
  value: string;
  label: string;
  options?: NestedOption[];
  isCollapsibleTriggerSelectable?: boolean;
};

export type NestedDropdownMenuProps = {
  options: NestedOption[];
  onSelected: (option: NestedOption) => void;
  className?: string;
  placeholder: string;
  selectedValue?: NestedOption;
  alignmentOptions?: DropdownMenuAlignmentOptions;
  triggerClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
};

export function NestedDropdownMenu({
  options,
  onSelected,
  className,
  placeholder,
  selectedValue,
  alignmentOptions,
  triggerClassName,
  contentClassName,
  disabled,
}: NestedDropdownMenuProps) {
  const [value, setValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setValue(selectedValue ? selectedValue.label : '');
  }, [selectedValue]);

  const handleSelected = (option: NestedOption, isOpen = false) => {
    setValue(option.label);
    onSelected(option);
    setIsOpen(isOpen);
  };

  return (
    <div
      className={twMerge(
        'flex w-full items-center justify-between rounded-lg border border-gray-5 align-middle',
        className,
        disabled && 'bg-gray-2'
      )}
    >
      <Root open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
        <Trigger
          asChild
          disabled={disabled}
          className={twMerge(
            'flex h-10 w-full items-center justify-between px-2 text-sm text-gray-8',
            triggerClassName,
            disabled && 'cursor-not-allowed'
          )}
        >
          <button className="flex items-center gap-2">
            {value === '' ? (
              <span className="text-gray-8">{placeholder}</span>
            ) : (
              value
            )}
            <AnimatePresence>
              <motion.div
                animate={isOpen ? 'opened' : 'closed'}
                variants={{
                  closed: { rotate: 0 },
                  opened: { rotate: -180 },
                }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDownIcon
                  color="gray-9"
                  className="h-4 w-4 text-gray-9"
                />
              </motion.div>
            </AnimatePresence>
          </button>
        </Trigger>

        <Portal className="absolute z-10 mt-2 w-fit">
          <Content {...(alignmentOptions ? alignmentOptions : {})}>
            <div
              className={twMerge(
                'mt-2 min-w-[256px] rounded border border-gray-4 bg-gray-1 shadow-lg-down',
                contentClassName
              )}
            >
              <NestedDropdownMenuItems
                options={options}
                onSelected={(option, isOpen = false) => {
                  handleSelected(option, isOpen);
                }}
                depth={0}
              />
            </div>
          </Content>
        </Portal>
      </Root>
    </div>
  );
}

type NestedDropdownMenuItemsProps = {
  options: NestedOption[];
  onSelected: (value: NestedOption, isOpen?: boolean) => void;
  depth: number;
};
export function NestedDropdownMenuItems({
  options,
  onSelected,
  depth,
}: NestedDropdownMenuItemsProps) {
  const [openCollapsibles, setOpenCollapsibles] = useState<string[]>([]);
  const handleNestedSelected = (option: NestedOption, isOpen = false) => {
    onSelected(option, isOpen);
  };

  const toggleCollapsible = (optionValue: string) => {
    if (openCollapsibles.includes(optionValue)) {
      setOpenCollapsibles(
        openCollapsibles.filter((val) => val !== optionValue)
      );
    } else {
      setOpenCollapsibles([...openCollapsibles, optionValue]);
    }
  };

  return (
    <div className="my-1 text-sm">
      {options.map((option) => (
        <div className="mx-auto rounded" key={option.value}>
          <CollapsibleRoot
            onOpenChange={() => toggleCollapsible(option.value)}
            open={openCollapsibles.includes(option.value)}
          >
            <CollapsibleTrigger
              className="w-full"
              onKeyDown={(e) => {
                if (
                  (!option.options || option.isCollapsibleTriggerSelectable) &&
                  e.key === 'Enter'
                ) {
                  onSelected(
                    option,
                    option.options && option.isCollapsibleTriggerSelectable
                  );
                }
              }}
              onClick={(e) => {
                if (!option.options || option.isCollapsibleTriggerSelectable) {
                  e.stopPropagation();
                  onSelected(
                    option,
                    option.options && option.isCollapsibleTriggerSelectable
                  );
                }
              }}
            >
              <div className="flex items-center justify-between px-4 text-gray-8 hover:bg-gray-3">
                <div
                  role="button"
                  style={{ paddingLeft: `${depth * 12}px` }}
                  tabIndex={0}
                  className="bg-gray-100 hover:bg-gray-300 flex h-10 items-center text-left "
                >
                  {option.label}
                </div>
                {option.options && option.options.length > 0 && (
                  <AnimatePresence>
                    <motion.div
                      animate={
                        openCollapsibles.includes(option.value)
                          ? 'opened'
                          : 'closed'
                      }
                      variants={{
                        closed: { rotate: 0 },
                        opened: { rotate: -180 },
                      }}
                      transition={{ duration: 0.2 }}
                      style={{ transformOrigin: 'center' }}
                    >
                      <ChevronDownIcon
                        color="gray-9"
                        className="h-4 w-4 text-gray-9"
                      />
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            </CollapsibleTrigger>

            {option.options && option.options.length > 0 && (
              <CollapsibleContent className="text-gray-500 border-gray-5">
                <NestedDropdownMenuItems
                  options={option.options}
                  onSelected={handleNestedSelected}
                  depth={depth + 1}
                />
              </CollapsibleContent>
            )}
          </CollapsibleRoot>
        </div>
      ))}
    </div>
  );
}
